import { AppError, IdSchema } from "@hisani/core";
import { z } from "zod";
import { STABLE_ERROR_CODES, StableOutboxEventSchema, } from "./contracts.js";
const DispatchStableOutboxInputSchema = z
    .object({ limit: z.number().int().min(1).max(10).default(10) })
    .strict();
const StableOutboxPublishResultSchema = z
    .object({
    acceptedIds: z.array(IdSchema),
    retryableFailureCount: z.number().int().nonnegative(),
    permanentFailureCount: z.number().int().nonnegative(),
})
    .strict();
/**
 * Mirrors `packages/marketplace/src/outbox-dispatch-use-case.ts`. Publishes
 * committed Stable outbox rows (invite sent/accepted, staff added/removed,
 * Entity Owner transfer requested/accepted/declined) to the pluggable
 * notification seam. As with Marketplace (ADR 0011), the AWS dispatch
 * infrastructure that actually invokes this is a separate, disabled-first
 * infra change — this use case only needs a real port implementation to run.
 */
export async function dispatchStableOutbox(input, dependencies) {
    const { limit } = DispatchStableOutboxInputSchema.parse(input);
    const pending = await listValidPendingEvents(limit, dependencies);
    if (pending.length === 0) {
        return { pendingCount: 0, publishedCount: 0 };
    }
    let publishResult;
    try {
        publishResult = await dependencies.publisher.publish(pending);
    }
    catch (cause) {
        throw stableOutboxUnavailable(cause);
    }
    const result = validatePublishResult(pending, publishResult);
    let publishedCount = 0;
    if (result.acceptedIds.length > 0) {
        try {
            const markedIds = z
                .array(IdSchema)
                .max(result.acceptedIds.length)
                .parse(await dependencies.repository.markPublished({
                system: "STABLE_OUTBOX_DISPATCHER",
                ids: result.acceptedIds,
                publishedAt: dependencies.clock.now(),
            }));
            const acceptedIds = new Set(result.acceptedIds);
            if (new Set(markedIds).size !== markedIds.length ||
                markedIds.some((id) => !acceptedIds.has(id))) {
                throw stableOutboxUnavailable();
            }
            publishedCount = markedIds.length;
        }
        catch (cause) {
            throw stableOutboxUnavailable(cause);
        }
    }
    if (result.retryableFailureCount > 0 || result.permanentFailureCount > 0) {
        throw stableOutboxUnavailable();
    }
    return { pendingCount: pending.length, publishedCount };
}
async function listValidPendingEvents(limit, dependencies) {
    let pending;
    try {
        pending = z
            .array(StableOutboxEventSchema)
            .max(limit)
            .parse(await dependencies.repository.listPending({
            system: "STABLE_OUTBOX_DISPATCHER",
            limit,
        }));
    }
    catch (cause) {
        throw stableOutboxUnavailable(cause);
    }
    if (new Set(pending.map(({ id }) => id)).size !== pending.length) {
        throw stableOutboxUnavailable();
    }
    return pending;
}
function validatePublishResult(pending, candidate) {
    const parsed = StableOutboxPublishResultSchema.safeParse(candidate);
    if (!parsed.success)
        throw stableOutboxUnavailable(parsed.error);
    const pendingIds = new Set(pending.map(({ id }) => id));
    const acceptedIds = new Set(parsed.data.acceptedIds);
    const validAccounting = acceptedIds.size === parsed.data.acceptedIds.length &&
        parsed.data.acceptedIds.every((id) => pendingIds.has(id)) &&
        parsed.data.acceptedIds.length +
            parsed.data.retryableFailureCount +
            parsed.data.permanentFailureCount ===
            pending.length;
    if (!validAccounting)
        throw stableOutboxUnavailable();
    return parsed.data;
}
function stableOutboxUnavailable(cause) {
    return new AppError({
        code: STABLE_ERROR_CODES.unavailable,
        message: "Stable notifications are temporarily unavailable.",
        ...(cause === undefined ? {} : { cause }),
    });
}
